import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ControlPointsService } from '@features/control-points/services/control-points-service';
import { FonctionsService } from '@shared/services/utilities/fonctions.service';
import { CurrentStateService } from 'app/core/services/current-state.service';
import { DeviceConfig } from '@shared/models/device-config';
import { DeviceConfigService } from '@shared/services/config/device-config.service';
import { Threshold } from '@shared/models/threshold';

@Component({
  selector: 'app-alarm-configuration',
  imports: [TranslateModule, ReactiveFormsModule, FormsModule,CommonModule,TranslateModule],
  templateUrl: './alarm-configuration.component.html',
  styleUrl: './alarm-configuration.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AlarmConfigurationComponent implements OnChanges {
  @Input() controlPointId: number | null = null;
  @Input() siteId: number | null = null;
  @Input() serialNumber?: string;
  @Input() deviceConfigForm?: FormGroup;

  triggerForm?: FormGroup;


  redAlertValue: number;
  yellowAlertValue: number;
  orangeAlertValue: number;
  isFormChanged = false;

  constructor(
    private fb: FormBuilder,
    private currentStateService: CurrentStateService,
    private fonctionsService: FonctionsService,
    private translate: TranslateService,
    private deviceConfigService: DeviceConfigService,
    private controlPointsService: ControlPointsService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['deviceConfigForm'] && this.deviceConfigForm && !this.triggerForm) {
      this.setupDeviceConfigForm();
      this.cdr.markForCheck();
    }
  }

  ngOnInit() {
    if (this.deviceConfigForm && !this.triggerForm) {
      this.setupDeviceConfigForm();
    }
    else if ((this.siteId != null && this.siteId !== undefined) || (this.controlPointId != null && this.controlPointId !== undefined)) {
      console.log(this.siteId, this.controlPointId);
      this.initForm();
    }
  }

  private setupDeviceConfigForm(): void {
    let emailsArray = this.deviceConfigForm.get('emailsThreshold');
    if (!emailsArray || !(emailsArray instanceof FormArray)) {
      this.deviceConfigForm.setControl('emailsThreshold', this.fb.array([]));
    }
    this.deviceConfigForm.valueChanges.subscribe(() => {
      this.isFormChanged = this.deviceConfigForm.dirty;
    });
  }

  get activeForm(): FormGroup {
    return this.triggerForm ?? this.deviceConfigForm;
  }

  onAlarmClick(email: AbstractControl, value: number) {
    const group = email as FormGroup;
    const alarmControl = group.get('alarm');
    if (!alarmControl) return;
    alarmControl.setValue(value);
    alarmControl.markAsTouched();
    alarmControl.markAsDirty();
    this.activeForm?.markAsDirty();
    this.isFormChanged = true;
  }

  initForm() {
    this.triggerForm = this.fb.group({
      triggerLevels: this.fb.array([]),
      emailsThreshold: this.fb.array([]),
      startHourNotif: [null, [Validators.min(0), Validators.max(23)]],
      startMinuteNotif: [null, [Validators.min(0), Validators.max(59)]],
      endHourNotif: [null, [Validators.min(0), Validators.max(23)]],
      endMinuteNotif: [null, [Validators.min(0), Validators.max(59)]],
      days: [null],
      controlPointId: [this.controlPointId],
      siteId: [this.siteId],
      id: [null],
      redAlertValue: [null],
      yellowAlertValue: [null],
      orangeAlertValue: [null],
    });
    this.loadThresholdData();
  }

  private async loadThresholdData() {
    const threshold = await this.controlPointsService.getThreshold(this.serialNumber, this.siteId, this.controlPointId);
    if (threshold) {
      this.triggerForm.patchValue({
        controlPointId: threshold.controlPointId,
        id: threshold.id,
        siteId: threshold.siteId,
        startHourNotif: threshold.startHourNotif,
        startMinuteNotif: threshold.startMinuteNotif,
        endHourNotif: threshold.endHourNotif,
        endMinuteNotif: threshold.endMinuteNotif,
        days: threshold.days,
        redAlertValue: threshold.redAlertValue,
        yellowAlertValue: threshold.yellowAlertValue,
        orangeAlertValue: threshold.orangeAlertValue
      });
      const emailsArray = this.triggerForm.get('emailsThreshold') as FormArray;
        emailsArray.clear();
        console.log(threshold);
        
        threshold.emailsThreshold.forEach(email => {
          const group = this.createEmailGroup(email);  
          emailsArray.push(group);
      });
      const triggersArray = this.triggerForm.get('triggerLevels') as FormArray;
      triggersArray.clear();
      threshold.triggerLevels.forEach(trigger => {
        triggersArray.push(this.fb.group(trigger));
      });
    }
    this.isFormChanged = false;
    this.triggerForm.valueChanges.subscribe(() => {
      this.isFormChanged = this.triggerForm.dirty;
      this.cdr.markForCheck();
    });
    this.cdr.markForCheck();
  }

  get emailsThreshold(): FormArray {
    if (this.deviceConfigForm) {
      return this.deviceConfigForm.get('emailsThreshold') as FormArray;
    }
    else if (this.triggerForm) {
      return this.triggerForm.get('emailsThreshold') as FormArray;
    }
    console.log(this.fb.array([]));
    
    return this.fb.array([]);
  }

  protected addEmail() {
    const email = this.fb.group({
      id: [null],
      isActive: [false],
      email: ['', [Validators.email]],
      alarm: [null],
      configId: [this.activeForm?.value.id],
      thresholdId: [this.triggerForm ? this.triggerForm.value.id : null]
    });
    if (!this.activeForm) return;
    let emailsArray = this.activeForm.get('emailsThreshold');
    if (!emailsArray || !(emailsArray instanceof FormArray)) {
      this.activeForm.setControl('emailsThreshold', this.fb.array([]));
      emailsArray = this.activeForm.get('emailsThreshold');
    }
    (emailsArray as FormArray).push(email);
  }

  protected removeEmail(index: number) {
    this.emailsThreshold.removeAt(index);
  }

  private createEmailGroup(emailData: any = { email: '', isActive: false }) {
    const group = this.fb.group({
      id: [emailData.id],
      thresholdId: [emailData.thresholdId],
      email: [{value: emailData.email,disabled: !emailData.isActive}],
      isActive: [emailData.isActive],
      alarm: [emailData.alarm],
      // configId: [this.deviceConfigForm.value.id]
    });
    const updateEmailValidators = (isActive: boolean) => {
      const emailControl = group.get('email');
      if (isActive) {
        emailControl.setValidators([Validators.required, Validators.email]);
        emailControl.enable();
      } else {
        emailControl.clearValidators();
        emailControl.disable();
      }
      emailControl.updateValueAndValidity();
    };
    updateEmailValidators(emailData.isActive);
    group.get('isActive').valueChanges.subscribe(updateEmailValidators);
    return group;
  }

  async submitForm() {
    this.currentStateService.setShowLoading(true);
    try {
      if (this.triggerForm != null) {
        // Save triggerForm (threshold logic)
        if (this.triggerForm.pristine) {
          this.currentStateService.setShowLoading(false);
          return;
        }
        let threshold = this.triggerForm.getRawValue() as Threshold;
        console.log(threshold);
        
        if (threshold.id == null) {
          await this.controlPointsService.addThreshold(threshold);
        } else {
          if (threshold.controlPointId != null) {
            await this.controlPointsService.updateThreshold(threshold);
          } else if (this.controlPointId != null) {
            threshold.controlPointId = this.controlPointId;
            threshold.siteId = null;
            threshold.id = null;
            threshold.triggerLevels?.forEach(trigger => {
              trigger.id = null;
              trigger.thresholdId = null;
            });
            await this.controlPointsService.addThreshold(threshold);
          } else {
            await this.controlPointsService.updateThreshold(threshold);
          }
        }
        this.currentStateService.setShowLoading(false);
        this.fonctionsService.launch_toast(true, this.translate.instant('TRIGGER.ALARM_SAVED'));
      } else {
        // Save deviceConfigForm
        if (!this.deviceConfigForm || this.deviceConfigForm.pristine) {
          this.currentStateService.setShowLoading(false);
          return;
        }
        this.deviceConfigForm.patchValue({
          redAlertValue: this.deviceConfigForm.value.redAlertValue ? Number(this.deviceConfigForm.value.redAlertValue) : null,
          yellowAlertValue: this.deviceConfigForm.value.yellowAlertValue ? Number(this.deviceConfigForm.value.yellowAlertValue) : null,
          orangeAlertValue: this.deviceConfigForm.value.orangeAlertValue ? Number(this.deviceConfigForm.value.orangeAlertValue) : null,
          emailsThreshold: this.deviceConfigForm.value.emailsThreshold,
          deviceSerial: this.deviceConfigForm.value.deviceSerial,
          siteId: this.deviceConfigForm.value.siteId
        });
        const formValue = this.deviceConfigForm.getRawValue();
        const deviceConfig: DeviceConfig = {
          ...formValue,
          langue: this.translate.currentLang ?? 'en'
        };
        const response = await this.deviceConfigService.updateDeviceConfiguration(deviceConfig).toPromise();
        if (response?.success) {
          this.currentStateService.setShowLoading(false);
          this.fonctionsService.launch_toast(true, this.translate.instant('DEVICE_CONFIG.SAVE_SUCCESS'));
        } else {
          this.currentStateService.setShowLoading(false);
          throw new Error(response?.message || 'Update failed');
        }
      }
    } catch (error) {
      this.currentStateService.setShowLoading(false);
      this.fonctionsService.launch_toast(false, this.translate.instant('TRIGGER.THRESHOLD_ERROR'));
    }
  }

  resetForm(): void {
    if (this.deviceConfigForm) {
      this.deviceConfigForm.reset();
    }
  }
}
